import { Grid } from '@material-ui/core';
import { Dispatch, useContext, VFC } from 'react';
import { useForm, UseFormReturn, FormProvider } from 'react-hook-form';
import { useHooks } from './useHooks';
import { useStyles } from './useStyles';
import { SiteProcessTextfield } from '../../input/ProcessTextfield';
import { SiteProcessSelectfield } from '../../input/ProcessSelectfield';
import { SiteProcessDatePicker } from '../../input/ProcessDatePicker';
import {
  DECIMAL_VALIDATION,
  validationMessage
} from '../../../../const/ProcessConst';
import {
  TEditSiteProcessForm,
  TSiteProcessDetailData
} from '../../../../types/process';
import { Link } from 'react-router-dom';
import { BaseModal } from '../../../molecules/BaseModal';
import { SiteProcessDeleteModal } from '../ProcessDeleteModal';
import { TSelectItem } from '../../../../types/common';
import { AuthInfoContext } from '../../../../AuthContextProvider';
import { ROLE } from '../../../../const/Const';

export type Props = {
  isOpen: boolean;
  setIsOpen: Dispatch<boolean>;
  afterUpdate: (updatedSiteProcessDetailData: TSiteProcessDetailData) => void;
  siteProcessId: number;
  afterDelete: () => void; // 削除モーダル用
};

export const SiteProcessEditModal: VFC<Props> = props => {
  const { isOpen, setIsOpen, afterUpdate, siteProcessId, afterDelete } = props;
  const classes = useStyles();
  const [authInfo, setAuthInfo] = useContext(AuthInfoContext);

  const methods: UseFormReturn<TEditSiteProcessForm> = useForm({
    reValidateMode: 'onSubmit'
  });
  const {
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors }
  } = methods;

  const {
    cancelUpdate,
    update,
    parentSiteProccessList,
    employeeList,
    endDate,
    handleChangeEndDatePicker,
    siteProcessDetail,
    statusList,
    isOpenSiteProcessDeleteModal,
    setIsOpenSiteProcessDeleteModal,
    handleDeleteSubmit,
    handleAfterDelete
  } = useHooks({
    setIsOpen,
    afterUpdate,
    getValues,
    setValue,
    reset,
    isOpen,
    siteProcessId,
    afterDelete
  });

  // 工程の担当者（担当者プルダウン用）
  const staffData: TSelectItem | null = siteProcessDetail?.charge_user
    ? {
        label: siteProcessDetail.charge_user.name,
        value: siteProcessDetail.charge_user.id
      }
    : null;

  return (
    <BaseModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      handleSubmit={handleSubmit(update, (erros: any) => {})}
      handleCancelSubmit={cancelUpdate}
      title={'工程編集'}
      buttonCaption={'更新する'}
      cancelButtonCaption={'キャンセル'}
      subDeleteButton={true}
      handleDeleteSubmit={handleDeleteSubmit}
      editFlg={authInfo?.role != ROLE['OUTSIDE_PARTNER']}
    >
      <FormProvider {...methods}>
        {/* タスク */}
        {siteProcessDetail && authInfo?.role != ROLE['OUTSIDE_PARTNER'] && (
          <Grid item xs={10} md={10} className={classes.task}>
            <Link
              to={{
                pathname: `/constructions/${siteProcessDetail.constructs.id}/tasks`,
                state: {
                  siteProcessId: siteProcessId
                }
              }}
            >
              登録済タスク：
              {siteProcessDetail.site_task_summary.site_task_count}
              件（未完了：
              {siteProcessDetail.site_task_summary.site_task_uncompleted_count}
              件）
            </Link>
          </Grid>
        )}
        {/* タスク */}
        <Grid container alignItems='center' justify='center'>
          {/* カテゴリ */}
          <Grid item xs={3} md={3} className={classes.gridItem}>
            <label className={classes.label}>工程</label>
          </Grid>
          {/* カテゴリ */}
          <Grid item xs={7} md={7} className={classes.gridItem}>
            {siteProcessDetail?.parent_site_process ? (
              <label className={classes.label}>小工程</label>
            ) : (
              <label className={classes.label}>大工程</label>
            )}
          </Grid>
        </Grid>
        {/* 大工程 */}
        {siteProcessDetail?.parent_site_process && (
          <Grid container alignItems='center' justify='center'>
            <Grid item xs={10} md={3} className={classes.gridItem}>
              <label className={classes.label}>大工程*</label>
            </Grid>
            <Grid item xs={10} md={7}>
              <SiteProcessSelectfield
                name='parent_site_process_id'
                listDatas={parentSiteProccessList}
                className={`${classes.input} ${classes.gridItem}`}
                required={true}
                error={'parent_site_process_id' in errors}
                errorMessage={
                  errors?.parent_site_process_id && validationMessage.required
                }
                disabled={authInfo?.role == ROLE['OUTSIDE_PARTNER']}
              />
            </Grid>
          </Grid>
        )}
        {/* /大工程 */}
        {/* 工程名 */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.label}>工程名*</label>
        </Grid>
        <Grid item xs={10} md={7}>
          <SiteProcessTextfield
            name='site_process_name'
            className={`${classes.input} ${classes.gridItem}`}
            required={true}
            maxLength={100}
            errorMessage={errors?.site_process_name?.message}
            disabled={authInfo?.role == ROLE['OUTSIDE_PARTNER']}
          />
        </Grid>
        {/* /工程名 */}
        {/* 担当者 */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.label}>担当者</label>
        </Grid>
        <Grid item xs={10} md={7}>
          <SiteProcessSelectfield
            name='charge_user_id'
            listDatas={employeeList}
            className={`${classes.input} ${classes.gridItem}`}
            required={false}
            error={false}
            errorMessage={''}
            disabled={authInfo?.role == ROLE['OUTSIDE_PARTNER']}
            staffData={staffData}
          />
        </Grid>
        {/* /担当者 */}
        {/* 期間 */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.label}>期間*</label>
        </Grid>
        <Grid item xs={10} md={7}>
          <div className={classes.leftAlignContainer}>
            <Grid container>
              <Grid item xs={7} md={5}>
                <div className={classes.itemMargin}>
                  <SiteProcessDatePicker
                    name='start_date'
                    label='開始日'
                    required={true}
                    fullWidth={true}
                    toDate={endDate}
                    errorMessage={errors?.start_date?.message}
                    className={classes.gridItem}
                    errorMessageStyle={{ display: 'none' }}
                    disabled={authInfo?.role == ROLE['OUTSIDE_PARTNER']}
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={1}>
                <div className={classes.itemMargin} style={{ width: 20 }}>
                  <span className={classes.textStyle}>〜</span>
                </div>
              </Grid>
              <Grid item xs={7} md={5}>
                <div className={classes.itemMargin}>
                  <SiteProcessDatePicker
                    name='end_date'
                    label='終了日'
                    fullWidth={true}
                    onChange={handleChangeEndDatePicker}
                    className={classes.gridItem}
                    disabled={authInfo?.role == ROLE['OUTSIDE_PARTNER']}
                  />
                </div>
              </Grid>
              {errors.start_date && (
                <Grid item xs={12}>
                  <div className={`${classes.helperText} ${classes.gridItem}`}>
                    {errors?.start_date?.message}
                  </div>
                </Grid>
              )}
            </Grid>
          </div>
        </Grid>
        {/* /期間 */}
        {/* 計画人工 */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.label}>計画人工</label>
        </Grid>
        <Grid item xs={10} md={7}>
          <SiteProcessTextfield
            name='planned_man_day'
            className={`${classes.input} ${classes.gridItem}`}
            pattern={DECIMAL_VALIDATION}
            patternMessage={validationMessage.decimal}
            max={9999.99}
            errorMessage={errors?.planned_man_day?.message}
            disabled={authInfo?.role == ROLE['OUTSIDE_PARTNER']}
          />
        </Grid>
        {/* /計画人工 */}
        {/* 実働人工 */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.label}>実働人工</label>
        </Grid>
        <Grid item xs={10} md={7}>
          <SiteProcessTextfield
            name='actual_man_day'
            className={`${classes.input} ${classes.gridItem}`}
            pattern={DECIMAL_VALIDATION}
            patternMessage={validationMessage.decimal}
            max={9999.99}
            errorMessage={errors?.actual_man_day?.message}
            disabled={authInfo?.role == ROLE['OUTSIDE_PARTNER']}
          />
        </Grid>
        {/* /実働人工 */}
        {/* 備考 */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.label}>備考</label>
        </Grid>
        <Grid item xs={10} md={7}>
          <SiteProcessTextfield
            name='note'
            className={`${classes.input} ${classes.gridItem}`}
            required={false}
            maxLength={500}
            errorMessage={errors?.note?.message}
            disabled={authInfo?.role == ROLE['OUTSIDE_PARTNER']}
          />
        </Grid>
        {/* /備考 */}
        {/* ステータス */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <div className={classes.label}>ステータス*</div>
        </Grid>
        <Grid item xs={10} md={7}>
          <SiteProcessSelectfield
            name='status'
            listDatas={statusList.data}
            className={`${classes.input} ${classes.gridItem}`}
            required={true}
            error={'status' in errors}
            errorMessage={errors?.status && validationMessage.required}
            disabled={authInfo?.role == ROLE['OUTSIDE_PARTNER']}
          />
        </Grid>
        {/* /ステータス */}
      </FormProvider>
      {/* 削除モーダル */}
      <SiteProcessDeleteModal
        isOpen={isOpenSiteProcessDeleteModal}
        siteProcessId={siteProcessId}
        setIsOpen={setIsOpenSiteProcessDeleteModal}
        afterSubmit={handleAfterDelete}
      />
    </BaseModal>
  );
};
